import { useDispatch } from 'react-redux';
import { useCallback, useEffect, useState } from 'react';
import { Link as RouterLink, useLocation, useParams } from 'react-router-dom';
import { Card, Container, Stack, Link, Typography, Grid, styled } from '@mui/material';
import { Box } from '@mui/system';

// Material Icons
import EventNoteIcon from '@mui/icons-material/EventNote';
import CalendarTodayIcon from '@mui/icons-material/CalendarToday';
import GroupIcon from '@mui/icons-material/Group';
import PersonIcon from '@mui/icons-material/Person';
import AccountBalanceIcon from '@mui/icons-material/AccountBalance';
import LocationCityIcon from '@mui/icons-material/LocationCity';
import ReceiptIcon from '@mui/icons-material/Receipt';
import PriceCheckIcon from '@mui/icons-material/PriceCheck';
import CheckCircleIcon from '@mui/icons-material/CheckCircle';
import PaymentIcon from '@mui/icons-material/Payment';
import EmailIcon from '@mui/icons-material/Email';
import PhoneIcon from '@mui/icons-material/Phone';
import AssignmentIcon from '@mui/icons-material/Assignment';

import useLoginSelector from '../../store/Slice/Login/LoginSelector';
// components
import BrudCrumbs from '../../components/BreadCrumbs';
import Page from '../../components/Page';
import PageTitle from '../../components/PageHeading';
import Scrollbar from '../../components/Scrollbar';
import { getPayableViewData } from '../../store/Slice/Payable/PayableSlice';
import usePayableSelector from '../../store/Slice/Payable/PayableSelector';

function ViewPayable() {
  const location = useLocation();

  const [file, setFile] = useState();
  const dispatch = useDispatch();
  const { LoginData } = useLoginSelector();
  const { PayableViewData } = usePayableSelector();

  function handleChange(e) {
    setFile(URL.createObjectURL(e.target.files[0]));
  }

  const Input = styled('input')({
    display: 'none'
  });

  // console.log('rowId in view payable', location.state?.rowId?.paybleId);
  const payableId = location.state?.rowId?.paybleId;
  const status = location.state?.rowId?.payableStatus;

  const getBankListApi = useCallback(() => {
    dispatch(getPayableViewData(payableId));
  }, [payableId, dispatch]);

  useEffect(() => {
    getBankListApi();
  }, [getBankListApi]);

  return (
    <Page title="Partner Management System || Payout">
      <Container maxWidth="xl">
        <Box pt={3} pb={3} mb={4}>
          <Stack
            direction="row"
            alignItems="center"
            justifyContent="space-between"
            className="pageHeading"
          >
            <PageTitle info="View File" />
            <Box>
              <BrudCrumbs />
            </Box>
          </Stack>

          <Box container textAlign="right">
            <Box className="statusBox">
              <Typography variant="body2">Current Status:</Typography>
              <Typography variant="subtitle2" className="disbursedText">
                {status === 'PENDING' ? 'Pending' : status === 'PAID' ? 'Paid' : status}
              </Typography>
            </Box>
          </Box>

          <Typography variant="body1" className="headingLineUp headingLineUpOut">
            File Details
          </Typography>
          <Box className="viewLeadCard cardTopMinusMargin" mb={3}>
            <Grid container spacing={3}>
              {/* Bank Name */}
              <Grid item xs={4}>
                <Box className="displayFlex">
                  <AccountBalanceIcon className="viewLabelIcon" size={24} />
                  <Box>
                    <Typography variant="body2" className="labelText">
                      Bank Name
                    </Typography>
                    <Typography variant="subtitle2" className="labelValue">
                      {PayableViewData?.bankName || 'N/A'}
                    </Typography>
                  </Box>
                </Box>
              </Grid>

              {/* Borrower Name */}
              <Grid item xs={4}>
                <Box className="displayFlex">
                  <PersonIcon className="viewLabelIcon" size={24} />
                  <Box>
                    <Typography variant="body2" className="labelText">
                      Borrower Name
                    </Typography>
                    <Typography variant="subtitle2" className="labelValue">
                      {PayableViewData?.borrowerName || 'N/A'}
                    </Typography>
                  </Box>
                </Box>
              </Grid>

              {/* Branch Name */}
              <Grid item xs={4}>
                <Box className="displayFlex">
                  <LocationCityIcon className="viewLabelIcon" size={24} />
                  <Box>
                    <Typography variant="body2" className="labelText">
                      Branch Name
                    </Typography>
                    <Typography variant="subtitle2" className="labelValue">
                      {PayableViewData?.branchName || 'N/A'}
                    </Typography>
                  </Box>
                </Box>
              </Grid>

              {/* Created On */}
              <Grid item xs={4}>
                <Box className="displayFlex">
                  <CalendarTodayIcon className="viewLabelIcon" size={24} />
                  <Box>
                    <Typography variant="body2" className="labelText">
                      Created On
                    </Typography>
                    <Typography variant="subtitle2" className="labelValue">
                      {PayableViewData?.createdOn || 'N/A'}
                    </Typography>
                  </Box>
                </Box>
              </Grid>

              {/* Due Date */}
              <Grid item xs={4}>
                <Box className="displayFlex">
                  <EventNoteIcon className="viewLabelIcon" size={24} />
                  <Box>
                    <Typography variant="body2" className="labelText">
                      Due Date
                    </Typography>
                    <Typography variant="subtitle2" className="labelValue">
                      {PayableViewData?.dueDate || 'N/A'}
                    </Typography>
                  </Box>
                </Box>
              </Grid>

              {/* File No */}
              <Grid item xs={4}>
                <Box className="displayFlex">
                  <AssignmentIcon className="viewLabelIcon" size={24} />
                  <Box>
                    <Typography variant="body2" className="labelText">
                      File No
                    </Typography>
                    <Typography variant="subtitle2" className="labelValue">
                      {PayableViewData?.fileNo || 'N/A'}
                    </Typography>
                  </Box>
                </Box>
              </Grid>

              {/* GST Amount */}
              <Grid item xs={4}>
                <Box className="displayFlex">
                  <PriceCheckIcon className="viewLabelIcon" size={24} />
                  <Box>
                    <Typography variant="body2" className="labelText">
                      GST Amount
                    </Typography>
                    <Typography variant="subtitle2" className="labelValue">
                      {PayableViewData?.gstAmount || 'N/A'}
                    </Typography>
                  </Box>
                </Box>
              </Grid>

              {/* Invoice Amount */}
              <Grid item xs={4}>
                <Box className="displayFlex">
                  <ReceiptIcon className="viewLabelIcon" size={24} />
                  <Box>
                    <Typography variant="body2" className="labelText">
                      Invoice Amount
                    </Typography>
                    <Typography variant="subtitle2" className="labelValue">
                      {PayableViewData?.invoiceAmount || 'N/A'}
                    </Typography>
                  </Box>
                </Box>
              </Grid>

              {/* Invoice Date */}
              <Grid item xs={4}>
                <Box className="displayFlex">
                  <CalendarTodayIcon className="viewLabelIcon" size={24} />
                  <Box>
                    <Typography variant="body2" className="labelText">
                      Invoice Date
                    </Typography>
                    <Typography variant="subtitle2" className="labelValue">
                      {PayableViewData?.invoiceDate || 'N/A'}
                    </Typography>
                  </Box>
                </Box>
              </Grid>

              {/* Invoice No */}
              <Grid item xs={4}>
                <Box className="displayFlex">
                  <ReceiptIcon className="viewLabelIcon" size={24} />
                  <Box>
                    <Typography variant="body2" className="labelText">
                      Invoice No
                    </Typography>
                    <Typography variant="subtitle2" className="labelValue">
                      {PayableViewData?.invoiceNo || 'N/A'}
                    </Typography>
                  </Box>
                </Box>
              </Grid>

              {/* Loan Name */}
              <Grid item xs={4}>
                <Box className="displayFlex">
                  <AssignmentIcon className="viewLabelIcon" size={24} />
                  <Box>
                    <Typography variant="body2" className="labelText">
                      Loan Name
                    </Typography>
                    <Typography variant="subtitle2" className="labelValue">
                      {PayableViewData?.loanName || 'N/A'}
                    </Typography>
                  </Box>
                </Box>
              </Grid>

              {/* MIS Match Status */}
              <Grid item xs={4}>
                <Box className="displayFlex">
                  <CheckCircleIcon className="viewLabelIcon" size={24} />
                  <Box>
                    <Typography variant="body2" className="labelText">
                      MIS Match Status
                    </Typography>
                    <Typography variant="subtitle2" className="labelValue">
                      {PayableViewData?.misIsMatch ? 'Matched' : 'Not Matched'}
                    </Typography>
                  </Box>
                </Box>
              </Grid>

              {/* Paid On */}
              <Grid item xs={4}>
                <Box className="displayFlex">
                  <PaymentIcon className="viewLabelIcon" size={24} />
                  <Box>
                    <Typography variant="body2" className="labelText">
                      Paid On
                    </Typography>
                    <Typography variant="subtitle2" className="labelValue">
                      {PayableViewData?.paidOn || 'N/A'}
                    </Typography>
                  </Box>
                </Box>
              </Grid>

              {/* Partner Email */}
              <Grid item xs={4}>
                <Box className="displayFlex">
                  <EmailIcon className="viewLabelIcon" size={24} />
                  <Box>
                    <Typography variant="body2" className="labelText">
                      Partner Email
                    </Typography>
                    <Typography variant="subtitle2" className="labelValue">
                      {PayableViewData?.partnerEmail || 'N/A'}
                    </Typography>
                  </Box>
                </Box>
              </Grid>

              {/* Partner Mobile */}
              <Grid item xs={4}>
                <Box className="displayFlex">
                  <PhoneIcon className="viewLabelIcon" size={24} />
                  <Box>
                    <Typography variant="body2" className="labelText">
                      Partner Mobile
                    </Typography>
                    <Typography variant="subtitle2" className="labelValue">
                      {PayableViewData?.partnerMobile || 'N/A'}
                    </Typography>
                  </Box>
                </Box>
              </Grid>

              {/* Partner Name */}
              <Grid item xs={4}>
                <Box className="displayFlex">
                  <GroupIcon className="viewLabelIcon" size={24} />
                  <Box>
                    <Typography variant="body2" className="labelText">
                      Partner Name
                    </Typography>
                    <Typography variant="subtitle2" className="labelValue">
                      {PayableViewData?.partnerName || 'N/A'}
                    </Typography>
                  </Box>
                </Box>
              </Grid>

              {/* Partner Payout */}
              <Grid item xs={4}>
                <Box className="displayFlex">
                  <PaymentIcon className="viewLabelIcon" size={24} />
                  <Box>
                    <Typography variant="body2" className="labelText">
                      Partner Payout
                    </Typography>
                    <Typography variant="subtitle2" className="labelValue">
                      {PayableViewData?.partnerPayout || 'N/A'}
                    </Typography>
                  </Box>
                </Box>
              </Grid>

              {/* Payable Status */}
              <Grid item xs={4}>
                <Box className="displayFlex">
                  <CheckCircleIcon className="viewLabelIcon" size={24} />
                  <Box>
                    <Typography variant="body2" className="labelText">
                      Payable Status
                    </Typography>
                    <Typography variant="subtitle2" className="labelValue">
                      {PayableViewData?.payableStatus || 'N/A'}
                    </Typography>
                  </Box>
                </Box>
              </Grid>

              {/* Total Amount */}
              <Grid item xs={4}>
                <Box className="displayFlex">
                  <PriceCheckIcon className="viewLabelIcon" size={24} />
                  <Box>
                    <Typography variant="body2" className="labelText">
                      Total Amount
                    </Typography>
                    <Typography variant="subtitle2" className="labelValue">
                      {PayableViewData?.totalAmount || 'N/A'}
                    </Typography>
                  </Box>
                </Box>
              </Grid>
            </Grid>
          </Box>
        </Box>
      </Container>
    </Page>
  );
}
export default ViewPayable;
